import React from "react";
import PropTypes from "prop-types";
import { useLocation, matchPath } from "react-router-dom";
import configs from "./configs.js";
import * as ga4 from "./gtag.js";
import { getCookie } from "./cookieUtils.js";
import { classifyGlobalJourneyRoute } from "./globalJourneyRoutes.js";

const telemetryQueue = [];
let cookieConsentApproved = false;

function hasTrackingConsent() {
  const consentCookie = getCookie("cookie_consent_level");
  if (consentCookie) {
    try {
      const consentLevel = JSON.parse(decodeURIComponent(consentCookie));
      return consentLevel.tracking === true;
    } catch (e) {
      console.error("Error parsing cookie consent level:", e);
      return false;
    }
  }
  return false;
}

function hasApprovedTrackingConsent() {
  return cookieConsentApproved || hasTrackingConsent();
}

export async function initTelemetry() {
  if (configs.GA_MEASUREMENT_ID) {
    if (hasApprovedTrackingConsent()) {
      ga4.initialize(configs.GA_MEASUREMENT_ID);
    } else {
      telemetryQueue.push({ type: "init" });
    }
  }
}

export function processQueuedTelemetry() {
  cookieConsentApproved = true;
  const gaMeasurementId = configs.GA_MEASUREMENT_ID;
  if (gaMeasurementId) {
    // Ensure GA is initialized once consent is granted
    if (!window.gtag) {
      ga4.initialize(gaMeasurementId);
    }
  }

  if (!window.gtag) {
    return;
  }

  while (telemetryQueue.length > 0) {
    const item = telemetryQueue.shift();
    if (item.type === "page_view") {
      window.gtag("set", "page", item.page);
      window.gtag("set", "title", item.title);
      window.gtag("event", "page_view");
    } else if (item.type === "global_page_view") {
      ga4.sendGlobalJourneyPageView(item.globalMeasurementId, item.pageView);
    }
  }
}

export function trackEvent(eventAction, eventValue) {
  const telemetryEnabled = !!configs.GA_MEASUREMENT_ID && hasApprovedTrackingConsent() && window.gtag;
  if (!telemetryEnabled) return;
  window.gtag("event", eventAction, {
    event_category: "XR Creator",
    event_action: eventAction,
    event_label: eventValue
  });
}

export function Telemetry({ overridePage, overrideTitle }) {
  const telemetryEnabled = (!!configs.GA_MEASUREMENT_ID || !!configs.GA4_GLOBAL_MEASUREMENT_ID) && window.gtag;
  const location = useLocation();

  React.useEffect(() => {
    let localOverridePage, localOverrideTitle;

    if (matchPath(location.pathname, { path: "/projects/:projectId" })) {
      localOverridePage = "/projects/editor";
      localOverrideTitle = "Editor";
    }

    const page = `/xr-creator${localOverridePage || location.pathname}`;
    const title = localOverrideTitle ? "XR Creator | " + localOverrideTitle : document.title;

    if (hasApprovedTrackingConsent() && configs.GA_MEASUREMENT_ID) {
      if (window.gtag) {
        window.gtag("set", "page", page);
        window.gtag("set", "title", title);
        window.gtag("event", "page_view");
      } else {
        telemetryQueue.push({ type: "page_view", page, title });
      }
    }

    const globalMeasurementId = configs.GA4_GLOBAL_MEASUREMENT_ID;
    const globalJourneyRoute = classifyGlobalJourneyRoute(location, configs.ROUTER_BASE_PATH);
    if (hasApprovedTrackingConsent() && globalMeasurementId && globalJourneyRoute.shouldTrack) {
      if (window.gtag) {
        ga4.sendGlobalJourneyPageView(globalMeasurementId, globalJourneyRoute.pageView);
      } else {
        telemetryQueue.push({
          type: "global_page_view",
          globalMeasurementId,
          pageView: globalJourneyRoute.pageView
        });
      }
    }
  }, [location, overridePage, overrideTitle, telemetryEnabled]);

  return null;
}

Telemetry.propTypes = {
  match: PropTypes.object,
  overridePage: PropTypes.string,
  overrideTitle: PropTypes.string
};
